import { useLocalStorage } from 'usehooks-ts'

import { useQuery } from '@fv/client-core'
import type {
  ShipperComparableLoadsQuery,
  ShipperComparableLoadsResult,
} from '@fv/models'

import { shipperFetch } from '@/utils/shipperFetch'

export type ComparableLoadsQueryState = Required<
  Pick<
    ShipperComparableLoadsQuery,
    'bookedWindow' | 'laneToleranceRadiusMiles' | 'leadTimeFilter'
  >
>

export const DEFAULT_COMPARABLE_LOADS_QUERY: ComparableLoadsQueryState = {
  bookedWindow: '30-days',
  laneToleranceRadiusMiles: 25,
  leadTimeFilter: 'any',
}

const getShipperComparableLoads = async (
  loadId: string,
  query: ComparableLoadsQueryState,
) => {
  return await shipperFetch<ShipperComparableLoadsResult>(
    `/shipments/${loadId}/comparables`,
    { query },
  )
}

export const comparableQueryKeys = {
  all: ['shipper-comparable-loads'] as const,
  id: (loadId: string) => [...comparableQueryKeys.all, loadId] as const,
  query: (loadId: string, query: ComparableLoadsQueryState) =>
    [...comparableQueryKeys.id(loadId), query] as const,
}

const useComparableLoadsQuery = (
  loadId: string | undefined,
  query: ComparableLoadsQueryState,
  opts?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: comparableQueryKeys.query(loadId!, query),
    queryFn: () => getShipperComparableLoads(loadId!, query),
    enabled: Boolean(loadId) && (opts?.enabled ?? true),
    staleTime: Infinity,
  })
}

export const useComparableLoadPrefs = () => {
  return useLocalStorage<ComparableLoadsQueryState>(
    'comparable-load-prefs',
    DEFAULT_COMPARABLE_LOADS_QUERY,
  )
}

export const useComparableLoads = (loadId: string) => {
  const [prefs] = useComparableLoadPrefs()
  return useComparableLoadsQuery(loadId, prefs)
}
